import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cooperplaceApi } from 'services/api';
import { useParams } from 'react-router-dom';
import { styles } from './styles';
import VideoStep from './VideoStep';
import IdentificationStep from './IdentificationStep';
import QuestionsStep from './QuestionsStep';
import QuestionsFooter from './QuestionsFooter';
import PageHeader from './PageHeader';
import FeedbackMessages from './FeedbackMessages';
import FullscreenLoading from './FullscreenLoading';
import CertificatePreview from './CertificatePreview';
import useTrainingVideoGate from './Hooks/useTrainingVideoGate';
import {
  maskPhone,
  maskCpfCnpj,
  formatSouthAmericaPhone,
  maskDDI,
  maskInternationalDocument,
} from './Utils/masks';
import { validatePersonalDataDetailed } from './Utils/validators';
import {
  safeStr,
  getInputValue,
  isAnswered,
  hasBackAttachments,
  hasLocalFiles,
  isAttachmentRequiredByBackend,
  getQuestionVideoUrl,
  isWatchOnlyVideoQuestion,
  isTrainingPublicQuestionRequired,
  isParentQuestion,
  shouldShowSubQuestion,
  groupQuestionsWithSubQuestions,
  processParentQuestion,
  resetQuestionForRetry,
  getTrainingScorePercent,
  getCertificateUrlFromResponse,
  normalizeShowPayload,
  getUniqueLinkLockInfo,
  shouldUseOriginalQuestionIdsForPayload,
  getPayloadQuestionId,
} from './Utils/helper';
import { useColumns, useIsMobile } from './Utils/responsive';

export default function PublicTrainingChecklistResponsePage() {
  const { token: tokenParam } = useParams();
  const isMobile = useIsMobile(768);
  const cols = useColumns();
  const mobileInputProps = isMobile
    ? { fontSize: '16px', placeholderSize: '16px' }
    : {};

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const token = tokenParam;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [certificateUrl, setCertificateUrl] = useState('');
  const [model, setModel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState('form');

  const [person, setPerson] = useState({
    firstName: '',
    lastName: '',
    email: '',
    ddi: '55',
    ddd: '',
    phone: '',
    docType: 'cpfCnpj',
    docValue: '',
  });

  const [personErrors, setPersonErrors] = useState({});
  const [personTouched, setPersonTouched] = useState({});
  const [qTouched, setQTouched] = useState({});
  const [qErrors, setQErrors] = useState({});
  const [answered, setAnswered] = useState(false);
  const [canRetryTraining, setCanRetryTraining] = useState(false);
  const [uniqueLock, setUniqueLock] = useState({ blocked: false, reason: '' });
  const [identificationLocked, setIdentificationLocked] = useState(false);

  const locked = saving || answered || uniqueLock.blocked;

  const touchPersonField = useCallback(key => {
    setPersonTouched(t => ({ ...t, [key]: true }));
  }, []);

  const hasPersonError = useCallback(
    key => !!personErrors?.[key] && !!personTouched?.[key],
    [personErrors, personTouched]
  );

  const inputErrorStyle = useCallback(
    key => (hasPersonError(key) ? { border: '1px solid #FCA5A5' } : undefined),
    [hasPersonError]
  );

  const errorTextStyle = useMemo(
    () => ({ marginTop: 6, color: '#DC2626', fontSize: 12, fontWeight: 700 }),
    []
  );

  const showQuestionError = useCallback(
    id => !!qTouched?.[id] && !!qErrors?.[id],
    [qTouched, qErrors]
  );

  function touchQuestion(id) {
    setQTouched(t => ({ ...t, [id]: true }));
  }

  function getQuestionError(q) {
    const required = isTrainingPublicQuestionRequired(q);

    if (required && !isAnswered(q)) return 'Campo obrigatório.';

    const attachmentRequired = isAttachmentRequiredByBackend(q);
    if (attachmentRequired && !hasBackAttachments(q) && !hasLocalFiles(q))
      return 'Anexo obrigatório.';

    return '';
  }

  function validateAllQuestionsAndMark() {
    const nextErrors = {};
    const nextTouched = {};
    const grouped = groupQuestionsWithSubQuestions(questions);

    grouped.forEach(({ parent, subQuestions }) => {
      nextTouched[parent.id] = true;
      const err = getQuestionError(parent);
      if (err) nextErrors[parent.id] = err;

      const visibleSubs = subQuestions.filter(sq =>
        shouldShowSubQuestion(parent, sq)
      );
      visibleSubs.forEach(sub => {
        nextTouched[sub.id] = true;
        const subErr = getQuestionError(sub);
        if (subErr) nextErrors[sub.id] = subErr;
      });
    });

    setQTouched(nextTouched);
    setQErrors(nextErrors);

    return nextErrors;
  }

  const hydrateTrainingQuestions = useCallback(async rawQuestions => {
    const list = Array.isArray(rawQuestions) ? rawQuestions : [];

    return Promise.all(
      list.map(async question => {
        if (!isWatchOnlyVideoQuestion(question)) return question;
        if (getQuestionVideoUrl(question)) return question;

        const questionId =
          question?.originalQuestion?.id || question?.questionId;
        if (!questionId) return question;

        try {
          const { data } = await cooperplaceApi.get(`questions/${questionId}`);
          return {
            ...question,
            originalQuestion: {
              ...(question?.originalQuestion || {}),
              ...data,
              media: data?.media || question?.originalQuestion?.media,
            },
          };
        } catch (_error) {
          return question;
        }
      })
    );
  }, []);

  const applyLoadedChecklist = useCallback(normalized => {
    const lockInfo = getUniqueLinkLockInfo(normalized);
    setUniqueLock(lockInfo);

    const rawQuestions = normalized?.questionsResponses || [];
    const processedQuestions = rawQuestions.map(q =>
      isParentQuestion(q) ? processParentQuestion(q, rawQuestions) : q
    );

    setModel(normalized);
    setQuestions(processedQuestions);
    setIdentificationLocked(false);

    if (lockInfo.blocked) {
      setStep('questions');
      setAnswered(true);
      setFeedbackStatus('info');
      setError('');
      setQTouched({});
      setQErrors({});
      return;
    }

    setStep('form');

    setAnswered(false);
    setCanRetryTraining(false);
    setQTouched({});
    setQErrors({});
    setError('');
    setFeedbackStatus('');
    setCertificateUrl('');
  }, []);

  const fetchPublicChecklist = useCallback(async () => {
    setError('');
    setFeedbackStatus('');
    setLoading(true);

    if (!token || String(token).trim().length < 6) {
      setModel(null);
      setQuestions([]);
      setUniqueLock({ blocked: false, reason: '' });
      setError('Link inválido ou incompleto.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await cooperplaceApi.get(
        `/training-checklist-link/questions/${token}`
      );
      const normalized = normalizeShowPayload(data);

      if (!mountedRef.current) return;

      if (!normalized) {
        setModel(null);
        setQuestions([]);
        setUniqueLock({ blocked: false, reason: '' });
        setError('Checklist não encontrado.');
      } else {
        const hydratedQuestions = await hydrateTrainingQuestions(
          normalized?.questionsResponses || []
        );

        if (!mountedRef.current) return;

        applyLoadedChecklist({
          ...normalized,
          questionsResponses: hydratedQuestions,
        });
      }
    } catch (e) {
      if (!mountedRef.current) return;

      const backMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        (Array.isArray(e?.response?.data?.messages)
          ? e.response.data.messages.join(' • ')
          : '') ||
        '';

      setModel(null);
      setQuestions([]);
      setUniqueLock({ blocked: false, reason: '' });
      setError(backMsg ? safeStr(backMsg) : 'Não foi possível carregar.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [applyLoadedChecklist, hydrateTrainingQuestions, token]);

  useEffect(() => {
    fetchPublicChecklist();
  }, [fetchPublicChecklist]);

  function updateQuestion(questionId, fn) {
    setQuestions(prev => {
      const next = (prev || []).map(q => {
        if (q.id !== questionId) return q;

        const cloned = {
          ...q,
          responses: Array.isArray(q.responses)
            ? q.responses.map(r => ({ ...r }))
            : [],
          attachments: Array.isArray(q.attachments) ? [...q.attachments] : [],
          attachment: q.attachment ? { ...q.attachment } : {},
        };

        return fn(cloned) || cloned;
      });

      return next;
    });
  }

  const removeSingleFile = useCallback((questionId, fileIndex) => {
    touchQuestion(questionId);

    updateQuestion(questionId, q => {
      const filesArr = Array.from(q?.attachment?.files || []);
      filesArr.splice(fileIndex, 1);

      return {
        ...q,
        attachment: {
          ...q.attachment,
          files: filesArr.length ? filesArr : null,
        },
      };
    });
  }, []);

  function toggleUnique(questionId, optionId) {
    touchQuestion(questionId);
    updateQuestion(questionId, q => ({
      ...q,
      responses: (q.responses || []).map(r => ({
        ...r,
        selected: r.id === optionId,
      })),
    }));
  }

  function toggleMultiple(questionId, optionId) {
    touchQuestion(questionId);
    updateQuestion(questionId, q => ({
      ...q,
      responses: (q.responses || []).map(r => {
        if (r.id !== optionId) return r;
        return { ...r, selected: !r.selected };
      }),
    }));
  }

  function setDescriptive(questionId, value) {
    touchQuestion(questionId);
    updateQuestion(questionId, q => ({
      ...q,
      questionDescriptiveResponse: value,
    }));
  }

  function setUploadFiles(questionId, files) {
    touchQuestion(questionId);
    updateQuestion(questionId, q => ({
      ...q,
      attachment: { files, questionId: q.id },
      error: null,
      mediaError: null,
    }));
  }

  function setPersonField(key, opts = {}) {
    return arg => {
      const raw = getInputValue(arg);
      let next = raw;

      if (opts.mask === 'ddi') next = maskDDI(raw);
      if (opts.mask === 'phone') next = maskPhone(raw);
      if (opts.mask === 'cpfCnpj') next = maskCpfCnpj(raw);
      if (opts.mask === 'internationalDoc')
        next = maskInternationalDocument(raw, opts.ddi);

      setPerson(prev => {
        const nextPerson = { ...prev, [key]: next };

        if (key === 'ddi' && maskDDI(next) === '55') {
          nextPerson.docType = 'cpfCnpj';
          nextPerson.docValue = '';
        }

        setPersonErrors(prevErr => {
          if (!personTouched?.[key]) return prevErr;
          const nextErrors = validatePersonalDataDetailed(nextPerson);
          return { ...prevErr, [key]: nextErrors?.[key] };
        });

        return nextPerson;
      });
    };
  }

  const headerTitle = model?.name || 'Responder Checklist';
  const answerableQuestions = useMemo(
    () => (questions || []).filter(q => !isWatchOnlyVideoQuestion(q)),
    [questions]
  );
  const groupedQuestions = useMemo(
    () => groupQuestionsWithSubQuestions(answerableQuestions),
    [answerableQuestions]
  );
  const total = groupedQuestions.length;

  const answeredCount = useMemo(() => {
    return groupedQuestions.reduce((acc, { parent, subQuestions }) => {
      const parentAnswered = isAnswered(parent);
      const visibleSubs = subQuestions.filter(sq =>
        shouldShowSubQuestion(parent, sq)
      );
      const subsOk = visibleSubs.every(
        sq => !isTrainingPublicQuestionRequired(sq) || isAnswered(sq)
      );
      return acc + (parentAnswered && subsOk ? 1 : 0);
    }, 0);
  }, [groupedQuestions]);
  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  async function sendMedias(questionsWithMedia, checklistToken) {
    const formData = new FormData();
    let idx = 0;

    questionsWithMedia.forEach(q => {
      const filesArr = Array.from(q?.attachment?.files || []);
      filesArr.forEach(file => {
        formData.append(`file${idx}@questionId=${q.id}`, file);
        idx += 1;
      });
    });

    await cooperplaceApi.post(
      `/training-checklist-link/media/${checklistToken}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  }

  function buildLinkResponsePayload({ questions: qs, person: p }) {
    const useOriginalQuestionId = shouldUseOriginalQuestionIdsForPayload(model);
    const payloadQuestions = (qs || []).map(q => {
      const hasLocal = hasLocalFiles(q);
      const hasBack = hasBackAttachments(q);
      const desc = safeStr(q.questionDescriptiveResponse).trim();

      return {
        id: getPayloadQuestionId(q, useOriginalQuestionId),
        question: safeStr(q.question),
        required: isTrainingPublicQuestionRequired(q),
        needAttachment: !!q.needAttachment,
        sentAttachment: !!(hasLocal || hasBack),
        questionDescriptiveResponse: desc || null,
        type: { id: q?.type?.id, name: q?.type?.name, slug: q?.type?.slug },
        responses: (q.responses || []).map(r => ({
          id: r.id,
          selected: !!r.selected,
          description: safeStr(r.description),
        })),
      };
    });

    const payload = { questions: payloadQuestions };
    payload.name = `${safeStr(p.firstName).trim()} ${safeStr(
      p.lastName
    ).trim()}`.trim();

    const ddi = safeStr(p.ddi).trim() || '55';
    const rawPhone = safeStr(p.phone).trim();
    payload.phoneNumber = formatSouthAmericaPhone(rawPhone, ddi);
    payload.email = safeStr(p.email).trim();

    if (p.docType === 'cpfCnpj') {
      payload.cpfCnpj = safeStr(p.docValue).trim();
    } else {
      payload.internationalDocument = safeStr(p.docValue).trim();
      payload.documentType = 'international';
    }

    return payload;
  }

  function isSingleUseFromModel(m) {
    return !!m?.link?.responseUnique;
  }

  async function resolveCertificateUrlAfterSubmit(responseData) {
    const directUrl = getCertificateUrlFromResponse(responseData);
    if (directUrl) return directUrl;

    try {
      const { data } = await cooperplaceApi.get(
        `/training-checklist-link/questions/${token}`
      );

      return getCertificateUrlFromResponse(data);
    } catch (_error) {
      return '';
    }
  }

  async function handleSubmit(options = {}) {
    const { allowWithoutQuestions = false } = options;

    if (!model) return;
    if (answered) return;
    if (saving) return;
    if (uniqueLock.blocked) return;

    setError('');
    setFeedbackStatus('');

    if (!token || String(token).trim().length < 6) {
      setError('Link inválido.');
      return;
    }

    if (step !== 'questions' && !allowWithoutQuestions) return;

    const touchedAll = {
      firstName: true,
      lastName: true,
      email: true,
      ddi: true,
      phone: true,
      docType: true,
      docValue: true,
    };
    setPersonTouched(touchedAll);

    const errors = validatePersonalDataDetailed(person);
    setPersonErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Revise os campos obrigatórios.');
      return;
    }

    const errs = validateAllQuestionsAndMark();
    if (Object.keys(errs).length > 0) {
      setError('Revise os campos obrigatórios.');
      return;
    }

    setSaving(true);

    try {
      const payload = buildLinkResponsePayload({
        questions,
        person,
      });
      const response = await cooperplaceApi.post(
        `/training-checklist-link/response/${token}`,
        payload
      );

      const questionsWithMedia = questions.filter(q => hasLocalFiles(q));
      if (questionsWithMedia.length)
        await sendMedias(questionsWithMedia, token);

      const scorePercent = getTrainingScorePercent(questions);
      const hasMinimumScore = scorePercent === null || scorePercent >= 70;
      const singleUse = isSingleUseFromModel(model);

      if (hasMinimumScore) {
        const nextCertificateUrl = await resolveCertificateUrlAfterSubmit(
          response?.data
        );

        setFeedbackStatus('success');
        setCertificateUrl(nextCertificateUrl);
        setCanRetryTraining(false);
      } else {
        setFeedbackStatus('warning');
        setCertificateUrl('');
        setCanRetryTraining(!singleUse);
      }

      setAnswered(true);
    } catch (e) {
      const backMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        (Array.isArray(e?.response?.data?.messages)
          ? e.response.data.messages.join(' • ')
          : '') ||
        '';

      setError(
        backMsg
          ? safeStr(backMsg)
          : 'Não foi possível enviar as respostas do treinamento.'
      );
    } finally {
      setSaving(false);
    }
  }

  const videoGate = useTrainingVideoGate({
    answerableTotal: total,
    onOnlyVideoTrainingComplete: () =>
      handleSubmit({ allowWithoutQuestions: true }),
    questions,
    setIdentificationLocked,
    setStep,
    step,
  });
  const { watchOnlyVideoQuestionItem } = videoGate;

  async function handleNextFromForm() {
    setError('');
    setFeedbackStatus('');

    const touchedAll = {
      firstName: true,
      lastName: true,
      email: true,
      ddi: true,
      phone: true,
      docType: true,
      docValue: true,
    };
    setPersonTouched(touchedAll);

    const errors = validatePersonalDataDetailed(person);
    setPersonErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Revise os campos obrigatórios.');
      return;
    }

    if (watchOnlyVideoQuestionItem) {
      videoGate.startVideoStep();
      return;
    }

    setStep('questions');
  }

  function handleRetryTraining() {
    if (!canRetryTraining || saving || uniqueLock.blocked) return;

    setQuestions(prev => (prev || []).map(resetQuestionForRetry));
    setAnswered(false);
    setCanRetryTraining(false);
    setQTouched({});
    setQErrors({});
    setError('');
    setFeedbackStatus('');
    setCertificateUrl('');
    setStep('questions');
  }

  function handleRetryTrainingWithVideo() {
    if (!canRetryTraining || saving || uniqueLock.blocked) return;

    setQuestions(prev => (prev || []).map(resetQuestionForRetry));
    setAnswered(false);
    setCanRetryTraining(false);
    setQTouched({});
    setQErrors({});
    setError('');
    setFeedbackStatus('');
    setCertificateUrl('');
    videoGate.startVideoStep();
  }

  function handleBackToVideoAction() {
    if (canRetryTraining) {
      handleRetryTrainingWithVideo();
      return;
    }

    videoGate.handleBackToVideoReview();
  }

  function handleDownloadCertificate() {
    if (!certificateUrl) return;

    window.open(certificateUrl, '_blank', 'noopener,noreferrer');
  }

  const containerStyle = useMemo(
    () => ({
      ...(styles.container || {}),
      maxWidth: '100%',
      width: '100%',
      paddingLeft: isMobile ? 12 : 24,
      paddingRight: isMobile ? 12 : 24,
    }),
    [isMobile]
  );

  const gridStyle = useMemo(() => {
    const base =
      typeof styles.grid === 'function'
        ? styles.grid(isMobile)
        : styles.grid || {};
    return {
      ...base,
      display: 'grid',
      width: '100%',
      minWidth: 0,
      gap: isMobile ? 12 : 16,
      alignItems: 'stretch',
      gridTemplateColumns:
        cols === 1
          ? 'minmax(0, 1fr)'
          : cols === 2
            ? 'repeat(2, minmax(0, 1fr))'
            : 'repeat(3, minmax(0, 1fr))',
    };
  }, [isMobile, cols]);

  const topWrapStyle = useMemo(
    () => ({
      backgroundColor: '#0B2A4A',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      position: isMobile ? 'relative' : 'sticky',
      top: isMobile ? 'auto' : 0,
      zIndex: 9999,
      borderBottom: 'none',
      boxShadow: 'none',
    }),
    [isMobile]
  );

  const docTypeOptions = useMemo(() => {
    const base = [{ value: 'cpfCnpj', label: 'CPF/CNPJ (Brasil)' }];
    if (maskDDI(person.ddi) !== '55') {
      base.push({ value: 'international', label: 'Documento internacional' });
    }
    return base;
  }, [person.ddi]);

  const docTypeSelected = useMemo(
    () => docTypeOptions.find(o => o.value === person.docType) || null,
    [docTypeOptions, person.docType]
  );
  const showCertificatePreview = feedbackStatus === 'success';

  function handleBackToIdentification() {
    videoGate.exitVideoReviewMode();
    setIdentificationLocked(false);
    setStep('form');
  }

  return (
    <>
      <FullscreenLoading open={saving} text="Enviando respostas..." />

      <PageHeader
        answeredCount={answeredCount}
        headerTitle={headerTitle}
        isMobile={isMobile}
        pct={pct}
        topWrapStyle={topWrapStyle}
        total={total}
      />

      <div style={styles.page}>
        <div style={containerStyle}>
          <div style={styles.body}>
            {showCertificatePreview ? (
              <CertificatePreview
                certificateUrl={certificateUrl}
                handleDownloadCertificate={handleDownloadCertificate}
                isMobile={isMobile}
              />
            ) : (
              <FeedbackMessages
                cols={cols}
                error={error}
                feedbackStatus={feedbackStatus}
                isMobile={isMobile}
                loading={loading}
                model={model}
                uniqueLock={uniqueLock}
              />
            )}

            {!showCertificatePreview &&
              !loading &&
              model &&
              !uniqueLock.blocked && (
                <>
                  {step === 'video' && (
                    <VideoStep
                      {...videoGate}
                      answered={answered}
                      handleBackToIdentification={handleBackToIdentification}
                      headerTitle={headerTitle}
                      isMobile={isMobile}
                      model={model}
                      total={total}
                    />
                  )}

                  {step === 'form' && (
                    <IdentificationStep
                      docTypeOptions={docTypeOptions}
                      docTypeSelected={docTypeSelected}
                      errorTextStyle={errorTextStyle}
                      handleNextFromForm={handleNextFromForm}
                      hasPersonError={hasPersonError}
                      inputErrorStyle={inputErrorStyle}
                      isMobile={isMobile}
                      locked={locked}
                      mobileInputProps={mobileInputProps}
                      person={person}
                      personErrors={personErrors}
                      setPerson={setPerson}
                      setPersonField={setPersonField}
                      touchPersonField={touchPersonField}
                    />
                  )}

                  {step === 'questions' && (
                    <QuestionsStep
                      gridStyle={gridStyle}
                      groupedQuestions={groupedQuestions}
                      isMobile={isMobile}
                      locked={locked}
                      onRemoveFile={removeSingleFile}
                      onSetDescriptive={setDescriptive}
                      onSetUpload={setUploadFiles}
                      onToggleMultiple={toggleMultiple}
                      onToggleUnique={toggleUnique}
                      qErrors={qErrors}
                      showQuestionError={showQuestionError}
                      touchQuestion={touchQuestion}
                    />
                  )}
                </>
              )}
          </div>

          {!!model &&
            !showCertificatePreview &&
            !loading &&
            !uniqueLock.blocked &&
            step === 'questions' && (
              <QuestionsFooter
                answered={answered}
                answeredCount={answeredCount}
                canRetryTraining={canRetryTraining}
                certificateUrl={certificateUrl}
                handleBackToIdentification={handleBackToIdentification}
                handleBackToVideoReview={handleBackToVideoAction}
                handleRetryTraining={handleRetryTraining}
                handleSubmit={handleSubmit}
                identificationLocked={identificationLocked}
                isMobile={isMobile}
                locked={locked}
                saving={saving}
                total={total}
                uniqueLock={uniqueLock}
                watchOnlyVideoQuestionItem={watchOnlyVideoQuestionItem}
              />
            )}
        </div>
      </div>
    </>
  );
}
